/**
 * Public, no-login demo: /demo
 *
 * Shows the product's magic moment — the buyer-side quote link — from the buyer's
 * seat, then lets the visitor accept it and see exactly what the merchant gets.
 *
 * Deliberately self-contained: NO database reads, NO Shopify writes, NO tokens.
 * The sample data below is fictional. Accepting is pure client-side state, so this
 * route is safe to hit from an ad or a TikTok bio at any volume.
 *
 * Built mobile-first on purpose: short-form traffic is ~90% phone, and the real
 * buyer page (q.$token) uses a wide table that does not fit a 390px viewport.
 */
import { useState } from "react";
import { Link } from "react-router";
import { marketingLinks, MarketingShell, socialImageMeta } from "../components/marketing/layout";
import { BRAND, SITE_ORIGIN } from "../brand";

export const links = marketingLinks;

export const meta = () => [
  { title: "See a B2B quote your buyer can accept — Quote Desk demo" },
  {
    name: "description",
    content:
      "A live sample of the buyer-side quote link. Negotiated per-line pricing, one-click accept, and the Shopify draft order it becomes. No login, no install.",
  },
  { property: "og:title", content: "See a B2B quote your buyer can accept" },
  {
    property: "og:description",
    content: "The buyer's view of a Quote Desk quote. Accept it and see what the merchant gets. No login.",
  },
  { property: "og:type", content: "website" },
  { property: "og:url", content: `${SITE_ORIGIN}/demo` },
  { tagName: "link", rel: "canonical", href: `${SITE_ORIGIN}/demo` },
  { name: "robots", content: "index,follow" },
  ...socialImageMeta(),
];

type Line = {
  title: string;
  sku: string;
  qty: number;
  list: number;
  quoted: number;
};

// Fictional sample. Mirrors the shape of a real quote without any real data.
const LINES: Line[] = [
  { title: "Kraft shipping box · 12×9×4", sku: "BX-1294", qty: 500, list: 1.4, quoted: 1.09 },
  { title: "Bubble mailer, recycled", sku: "ML-0080", qty: 1000, list: 0.62, quoted: 0.48 },
  { title: 'Pallet wrap, 18" stretch', sku: "PW-1801", qty: 24, list: 28.0, quoted: 22.5 },
];

const money = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD" });

const lineTotal = (l: Line) => l.qty * l.quoted;
const listTotal = (l: Line) => l.qty * l.list;

export default function QuoteDeskDemo() {
  const [accepted, setAccepted] = useState(false);

  const quoted = LINES.reduce((s, l) => s + lineTotal(l), 0);
  const list = LINES.reduce((s, l) => s + listTotal(l), 0);
  const saved = list - quoted;

  return (
    <MarketingShell headerCta="appstore">
      <section className="qd-wrap qdemo">
        <p className="qdemo__eyebrow">Interactive demo · nothing is saved</p>
        <h1 className="qdemo__h1">This is what your buyer sees.</h1>
        <p className="qdemo__lead">
          You send one link. No login, no portal, no Shopify account for them. They open it on
          whatever device they have and accept in one tap — and it becomes a real Shopify draft
          order on your side.
        </p>

        {/* ---- the buyer's quote card ---- */}
        <article className="qdemo__card" aria-labelledby="qdemo-quote-heading">
          <header className="qdemo__cardHead">
            <div>
              <p className="qdemo__from">Northwind Packaging</p>
              <h2 id="qdemo-quote-heading" className="qdemo__num">
                Quote Q-1042
              </h2>
              <p className="qdemo__for">For Harbor Coffee Roasters</p>
            </div>
            <span className={accepted ? "qdemo__badge is-accepted" : "qdemo__badge"}>
              {accepted ? "Accepted" : "Awaiting your approval"}
            </span>
          </header>

          {accepted ? (
            <div className="qdemo__banner" role="status">
              <strong>Quote accepted.</strong> In the live app this exact click calls Shopify&rsquo;s{" "}
              <code>draftOrderCreate</code> — the merchant gets a real order with the negotiated
              prices locked, and the buyer pays through normal checkout.
            </div>
          ) : null}

          {/* Line items as cards, not a table — a 4-column table does not fit a phone. */}
          <ul className="qdemo__lines">
            {LINES.map((l) => (
              <li key={l.sku} className="qdemo__line">
                <div className="qdemo__lineTop">
                  <span className="qdemo__lineTitle">{l.title}</span>
                  <span className="qdemo__lineTotal">{money(lineTotal(l))}</span>
                </div>
                <div className="qdemo__lineMeta">
                  <span className="qdemo__sku">SKU {l.sku}</span>
                  <span aria-hidden>·</span>
                  <span>{l.qty.toLocaleString("en-US")} units</span>
                  <span aria-hidden>·</span>
                  <span>
                    <s className="qdemo__list">{money(l.list)}</s>{" "}
                    <strong className="qdemo__quoted">{money(l.quoted)}</strong> ea
                    <span className="qdemo__lock" title="Negotiated price, locked to this quote" aria-label="Negotiated price, locked to this quote">
                      {" "}🔒
                    </span>
                  </span>
                </div>
              </li>
            ))}
          </ul>

          <dl className="qdemo__totals">
            <div>
              <dt>List price</dt>
              <dd className="qdemo__strike">{money(list)}</dd>
            </div>
            <div>
              <dt>Negotiated total</dt>
              <dd className="qdemo__grand">{money(quoted)}</dd>
            </div>
            <div>
              <dt>Buyer saves</dt>
              <dd className="qdemo__save">{money(saved)}</dd>
            </div>
          </dl>

          {accepted ? (
            <button type="button" className="qdemo__reset" onClick={() => setAccepted(false)}>
              Reset the demo
            </button>
          ) : (
            <div className="qdemo__actions">
              <button type="button" className="qdemo__accept" onClick={() => setAccepted(true)}>
                Accept quote
              </button>
              <button type="button" className="qdemo__decline" onClick={() => setAccepted(false)}>
                Decline
              </button>
            </div>
          )}
          <p className="qdemo__fine">
            Sample data. This demo writes nothing and creates no order.
          </p>
        </article>

        {/* ---- what the merchant side gets ---- */}
        <section className="qdemo__after" aria-label="What happens on the merchant side">
          <h2 className="qdemo__h2">On your side, the moment they accept</h2>
          <ol className="qdemo__steps">
            <li>
              <strong>A real Shopify draft order</strong> — created via <code>draftOrderCreate</code>,
              with each negotiated unit price locked so nothing reprices at checkout.
            </li>
            <li>
              <strong>The B2B company attached</strong> — pulled from your Shopify companies, so
              repeat buyers keep their history.
            </li>
            <li>
              <strong>They pay through your normal checkout</strong> — no separate wholesale
              storefront, no migration, and no Shopify Plus.
            </li>
          </ol>
          <p className="qdemo__note">
            Shopify reserves B2B for Plus, which starts around $2,300/mo. Quote Desk runs the same
            quote-to-order loop on Basic, Grow and Advanced.
          </p>
          <div className="qdemo__cta">
            <a
              className="qdemo__install"
              href="https://apps.shopify.com/quote-desk"
              target="_blank"
              rel="noreferrer"
            >
              Install free on Shopify
            </a>
            <Link className="qdemo__secondary" to="/features">
              See all features
            </Link>
          </div>
          <p className="qdemo__fine">
            Questions? <a href={`mailto:${BRAND.supportEmail}`}>{BRAND.supportEmail}</a>
          </p>
        </section>
      </section>
    </MarketingShell>
  );
}
